import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo, breadcrumb, eventJsonLd, absolute } from "@/lib/seo";
import { MaskedLine } from "@/components/Reveal";
import { EDITIONS_SEO, EDITIONS_INTRO, EDITIONS_IMAGES } from "@/data/editions";
import { SectionSuperLune, SectionSolstice, SectionLieux, SectionGuides, SectionNiveaux, SectionFinale } from "@/components/EditionsSections";
import { useLocale } from "@/context/LocaleContext";

const aVenir = (e) => !e.annule && new Date(e.date_fin || e.date) >= new Date();

const EditionsLimitees = () => {
  const { locale } = useLocale();
  const [events, setEvents] = useState([]);
  useEffect(() => { api.events().then((l) => setEvents(l.filter(aVenir))).catch(() => {}); }, []);
  const superLune = events.find((e) => e.type === "stage" && /super lune/i.test(e.titre));
  const speciaux = events.filter((e) => (e.edition_speciale && e.type === "stage") || e.type === "lieu_ancien").slice(0, 6);
  useSeo({
    title: EDITIONS_SEO.title,
    description: EDITIONS_SEO.description,
    image: EDITIONS_IMAGES.hero,
    jsonLd: [
      breadcrumb([["Accueil", "/"], ["Éditions limitées", "/tambours/editions-limitees"]]),
      { "@type": "CollectionPage", name: "Éditions limitées — Tambours Entre Les Mondes", description: EDITIONS_SEO.description, url: absolute("/tambours/editions-limitees") },
      ...speciaux.map(eventJsonLd),
    ],
  });

  return (
    <div data-testid="editions-page">
      <section className="relative bg-earth grain overflow-hidden flex items-end min-h-[82vh]" data-testid="editions-hero">
        <img src={EDITIONS_IMAGES.hero} alt="Mains au travail sur une peau de tambour, à l'atelier" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-earth via-earth/55 to-earth/20" />
        <div className="relative z-[2] w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28 pt-44">
          <Link to="/offres/stage-fabrication-tambour" data-testid="editions-back" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/60 hover:text-sand transition-colors mb-8"><ArrowLeft className="w-3.5 h-3.5" /> Créer son tambour</Link>
          <h1 className="font-serif text-sand text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl" data-testid="editions-titre">
            <MaskedLine delay={0.2}>{locale === "en" ? "Limited editions —" : "Éditions limitées —"}</MaskedLine>
            <MaskedLine delay={0.32}>{locale === "en" ? "Entre Les Mondes drums" : "Tambours Entre Les Mondes"}</MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-7 max-w-2xl text-sand/80 text-base md:text-lg leading-relaxed font-serif italic" data-testid="editions-intro">
            « {EDITIONS_INTRO} »
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <a href="#super-lune" data-testid="editions-hero-cta" className="inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-sand transition-colors duration-500">Découvrir les prochaines éditions <ArrowRight className="w-4 h-4" /></a>
            <a href="#guides" data-testid="editions-hero-cta-2" className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full border border-sand/40 text-sand text-sm tracking-wide hover:bg-sand hover:text-earth transition-colors duration-300">Comprendre comment choisir son tambour</a>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-8" data-testid="editions-sommaire">
        <p className="lg:col-span-5 font-serif text-2xl lg:text-3xl text-bark leading-snug">Trois éditions, trois manières de laisser le moment décider du tambour.</p>
        <nav className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
          {[["#super-lune", "01", "Super Lune", "Création de nuit, à la pleine lune"], ["#solstice", "02", "Solstice", "Une journée de création au jour le plus long"], ["#lieux", "03", "Tambours des Lieux", "Une journée en plein air, sur un site choisi"]].map(([href, n, t, d]) => (
            <a key={href} href={href} data-testid={`editions-sommaire-${href.slice(1)}`} className="group border border-sand-dark rounded-2xl p-5 hover:border-terra transition-colors duration-300">
              <span className="font-mono text-[10px] tracking-[0.3em] text-terra">{n}</span>
              <span className="block font-serif text-xl text-bark mt-2">{t}</span>
              <span className="block text-xs text-fumee mt-1.5 leading-relaxed">{d}</span>
            </a>
          ))}
        </nav>
      </section>

      <SectionSuperLune event={superLune} />
      <SectionSolstice events={events} />
      <SectionLieux events={events} />
      <SectionGuides />
      <SectionNiveaux />
      <SectionFinale />
    </div>
  );
};

export default EditionsLimitees;
